import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Calculator,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Info,
  Percent,
  Receipt
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ExerciseCalculatorProps {
  optionType: 'ISO' | 'NSO'
  sharesAvailable: number
  strikePrice: number
  currentFMV: number
  onCalculate?: (results: ExerciseResults) => void
  className?: string
}

export interface ExerciseResults {
  sharesToExercise: number
  exerciseCost: number
  currentValue: number
  immediateGain: number
  ordinaryIncome: number
  capitalGains: number
  amtAdjustment: number
  estimatedAMT: number
  netProceeds: number
  cashlessSharesWithheld: number
  cashlessSharesReceived: number
  breakEvenPrice: number
}

export default function ExerciseCalculator({
  optionType,
  sharesAvailable,
  strikePrice,
  currentFMV,
  onCalculate,
  className
}: ExerciseCalculatorProps) {
  const [exerciseType, setExerciseType] = useState<'cash' | 'cashless'>('cash')
  const [sharesToExercise, setSharesToExercise] = useState('')
  const [ordinaryIncomeTaxRate, setOrdinaryIncomeTaxRate] = useState('37')
  const [capitalGainsTaxRate, setCapitalGainsTaxRate] = useState('20')
  const [amtRate, setAmtRate] = useState('28')
  const [results, setResults] = useState<ExerciseResults | null>(null)

  useEffect(() => {
    if (sharesToExercise && parseInt(sharesToExercise) > 0) {
      calculateExercise()
    } else {
      setResults(null)
    }
  }, [sharesToExercise, exerciseType, ordinaryIncomeTaxRate, capitalGainsTaxRate, amtRate])

  const calculateExercise = () => {
    const shares = parseInt(sharesToExercise) || 0
    if (shares <= 0 || shares > sharesAvailable) return

    const exerciseCost = shares * strikePrice
    const currentValue = shares * currentFMV
    const immediateGain = currentValue - exerciseCost

    let ordinaryIncome = 0
    let capitalGains = 0
    let amtAdjustment = 0
    let estimatedAMT = 0
    let netProceeds = 0
    let cashlessSharesWithheld = 0
    let cashlessSharesReceived = 0

    if (optionType === 'ISO') {
      // ISOs: No ordinary income on exercise, but AMT adjustment
      amtAdjustment = immediateGain
      estimatedAMT = (amtAdjustment * parseFloat(amtRate)) / 100
      
      if (exerciseType === 'cash') {
        // Cash exercise: pay exercise cost + potential AMT
        netProceeds = -exerciseCost - estimatedAMT
      } else {
        // Cashless exercise for ISOs triggers disqualifying disposition
        ordinaryIncome = immediateGain
        const ordinaryTax = (ordinaryIncome * parseFloat(ordinaryIncomeTaxRate)) / 100
        const totalTaxes = ordinaryTax
        
        // Calculate shares to withhold for taxes and exercise cost
        cashlessSharesWithheld = Math.ceil((exerciseCost + totalTaxes) / currentFMV)
        cashlessSharesReceived = shares - cashlessSharesWithheld
        netProceeds = cashlessSharesReceived * currentFMV
      }
    } else {
      // NSOs: Ordinary income on exercise
      ordinaryIncome = immediateGain
      const ordinaryTax = (ordinaryIncome * parseFloat(ordinaryIncomeTaxRate)) / 100
      
      if (exerciseType === 'cash') {
        netProceeds = currentValue - exerciseCost - ordinaryTax
      } else {
        // Cashless exercise
        cashlessSharesWithheld = Math.ceil((exerciseCost + ordinaryTax) / currentFMV)
        cashlessSharesReceived = shares - cashlessSharesWithheld
        netProceeds = cashlessSharesReceived * currentFMV
      }
    }

    // Break-even price (price at which you recover exercise cost + taxes)
    const totalCost = exerciseCost + (optionType === 'ISO' ? estimatedAMT : (ordinaryIncome * parseFloat(ordinaryIncomeTaxRate)) / 100)
    const breakEvenPrice = totalCost / shares + strikePrice

    const newResults: ExerciseResults = {
      sharesToExercise: shares,
      exerciseCost,
      currentValue,
      immediateGain,
      ordinaryIncome,
      capitalGains,
      amtAdjustment,
      estimatedAMT,
      netProceeds,
      cashlessSharesWithheld,
      cashlessSharesReceived,
      breakEvenPrice
    }

    setResults(newResults)
    onCalculate?.(newResults)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value))
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Input Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Exercise Calculator</CardTitle>
          <CardDescription>
            Calculate the cost and tax implications of exercising your {optionType} options
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Option Type</Label>
              <div className="mt-2">
                <Badge variant={optionType === 'ISO' ? 'default' : 'secondary'}>
                  {optionType === 'ISO' ? 'Incentive Stock Options' : 'Non-Qualified Stock Options'}
                </Badge>
              </div>
            </div>
            <div>
              <Label>Exercise Type</Label>
              <Select value={exerciseType} onValueChange={(value: 'cash' | 'cashless') => setExerciseType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash Exercise</SelectItem>
                  <SelectItem value="cashless">Cashless Exercise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Shares to Exercise</Label>
              <Input
                type="number"
                value={sharesToExercise}
                onChange={(e) => setSharesToExercise(e.target.value)}
                placeholder="0"
                max={sharesAvailable}
              />
              <p className="text-xs text-gray-500 mt-1">
                Available: {formatNumber(sharesAvailable)}
              </p>
            </div>
            <div>
              <Label>Strike Price</Label>
              <div className="mt-2">
                <p className="text-2xl font-semibold">{formatCurrency(strikePrice)}</p>
                <p className="text-xs text-gray-500">per share</p>
              </div>
            </div>
          </div>

          <div>
            <Label>Current Fair Market Value</Label>
            <div className="mt-2">
              <p className="text-2xl font-semibold text-green-600">{formatCurrency(currentFMV)}</p>
              <p className="text-xs text-gray-500">per share</p>
            </div>
          </div>

          {/* Tax Rate Settings */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-3">Tax Rate Assumptions</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Ordinary Income</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={ordinaryIncomeTaxRate}
                    onChange={(e) => setOrdinaryIncomeTaxRate(e.target.value)}
                    className="h-8"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
              {optionType === 'ISO' && (
                <div>
                  <Label className="text-xs">AMT Rate</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={amtRate}
                      onChange={(e) => setAmtRate(e.target.value)}
                      className="h-8"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Capital Gains</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={capitalGainsTaxRate}
                    onChange={(e) => setCapitalGainsTaxRate(e.target.value)}
                    className="h-8"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Exercise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Exercise Cost</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.exerciseCost)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Value</p>
                  <p className="text-xl font-semibold text-green-600">{formatCurrency(results.currentValue)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Immediate Gain</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.immediateGain)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Break-Even Price</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.breakEvenPrice)}</p>
                </div>
              </div>

              {exerciseType === 'cashless' && results.cashlessSharesReceived > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Cashless Exercise Result</p>
                      <p className="text-sm text-blue-700">
                        {formatNumber(results.cashlessSharesWithheld)} shares withheld for cost & taxes
                      </p>
                      <p className="text-sm text-blue-700">
                        {formatNumber(results.cashlessSharesReceived)} shares received
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tax Implications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tax Implications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {optionType === 'ISO' && exerciseType === 'cash' ? (
                  <>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">AMT Adjustment</p>
                        <p className="text-sm text-gray-500">Bargain element for AMT calculation</p>
                      </div>
                      <p className="font-semibold">{formatCurrency(results.amtAdjustment)}</p>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">Estimated AMT</p>
                        <p className="text-sm text-gray-500">At {amtRate}% rate</p>
                      </div>
                      <p className="font-semibold text-orange-600">{formatCurrency(results.estimatedAMT)}</p>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        ISOs may trigger Alternative Minimum Tax. Consult a tax advisor for your specific situation.
                      </AlertDescription>
                    </Alert>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">Ordinary Income</p>
                        <p className="text-sm text-gray-500">Taxed at {ordinaryIncomeTaxRate}%</p>
                      </div>
                      <p className="font-semibold">{formatCurrency(results.ordinaryIncome)}</p>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">Estimated Tax</p>
                        <p className="text-sm text-gray-500">Federal income tax withholding</p>
                      </div>
                      <p className="font-semibold text-red-600">
                        {formatCurrency((results.ordinaryIncome * parseFloat(ordinaryIncomeTaxRate)) / 100)}
                      </p>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between py-2 bg-gray-50 rounded-lg px-3">
                  <p className="font-medium">Net Proceeds</p>
                  <p className={cn(
                    "text-xl font-bold",
                    results.netProceeds >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatCurrency(results.netProceeds)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Important Considerations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {optionType === 'ISO' && (
                  <>
                    <div className="flex items-start gap-2">
                      <Receipt className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium">Holding Period for Capital Gains</p>
                        <p className="text-gray-600">
                          Hold shares for 1 year after exercise AND 2 years after grant date for favorable tax treatment
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium">Disqualifying Disposition</p>
                        <p className="text-gray-600">
                          Selling before meeting holding periods converts gain to ordinary income
                        </p>
                      </div>
                    </div>
                  </>
                )}
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">State Taxes</p>
                    <p className="text-gray-600">
                      Additional state taxes may apply depending on your location
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Tax Advice</p>
                    <p className="text-gray-600">
                      This is an estimate only. Consult a tax professional for advice specific to your situation
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}